import { Injectable } from '@angular/core';
import { KeychainAccess, SecureStorage, StorageErrorType } from '@aparajita/capacitor-secure-storage';

import { Vault, VaultError } from './vault';

/**
 * The namespace every Vault item is stored under.
 *
 * `keys()` is answered by the store itself and returns only keys carrying this prefix, stripped of
 * it — which is why there is no key index to maintain. The `@capgo` implementation this replaces had
 * to keep one in `Preferences`, because reading a key to find out whether it existed would have
 * raised a prompt.
 */
export const VAULT_KEY_PREFIX = 'ninety.auth.';

/**
 * The prefix-scoped clear, which `SecureStoragePlugin` does not declare although both platforms
 * implement it — see {@link SecureStorageVault.purge} for why it is called directly.
 *
 * Carrying the same prefix under both names is not a style choice: the two platforms read different
 * option keys, and sending only the documented one makes Android delete every Keystore alias the app
 * owns. Intersected with the real plugin type rather than asserted through `unknown`, so this stays a
 * claim about one missing method instead of throwing the object's real type away.
 */
type PrefixScopedClear = {
  clearItemsWithPrefix(options: { readonly prefix: string; readonly _prefix: string }): Promise<void>;
};

/**
 * The only native Vault. It never prompts, because nothing stored here is access-controlled.
 *
 * Protection is at rest and nothing else: iOS holds each item as a Keychain item, Android encrypts
 * it with a non-exportable `AndroidKeyStore` key and writes the ciphertext to `SharedPreferences`.
 * A biometric protects the *app* instead — see ADR 0003 for why that trade was made.
 *
 * **`whenUnlockedThisDeviceOnly`, and not the default.** The implementation this replaces
 * set no accessibility attribute at all, so its items inherited Apple's `WhenUnlocked` default,
 * which does not end in `ThisDeviceOnly` — meaning a Durable Root travelled in an encrypted backup
 * and could be restored onto other hardware. `ThisDeviceOnly` ends that.
 *
 * It is deliberately **not** `whenPasscodeSetThisDeviceOnly`, which is stricter and would be wrong:
 * it cannot be written at all on a device with no passcode, and the OS destroys every item in that
 * class when the passcode is removed. Both outcomes are Forced Sign-outs.
 *
 * Android ignores the option — it is iOS-only, and Android's key cannot leave the device regardless.
 */
@Injectable()
export class SecureStorageVault implements Vault {
  /**
   * Configuration is per-process and idempotent, but the plugin call is not free, so it is done once
   * and awaited by every operation. The promise is held rather than a boolean, so two concurrent
   * first calls await the same configuration instead of racing it.
   */
  #configured: Promise<void> | null = null;

  async read(key: string): Promise<string | null> {
    try {
      await this.#configure();
      return await SecureStorage.getItem(key);
    } catch (error) {
      return this.absentOrThrow(error);
    }
  }

  async write(key: string, value: string): Promise<void> {
    try {
      await this.#configure();
      await SecureStorage.setItem(key, value);
    } catch (error) {
      throw new VaultError(this.classify(error) ?? { kind: 'vaultUnavailable' }, 'Failed to write to the vault');
    }
  }

  async remove(key: string): Promise<void> {
    try {
      await this.#configure();
      await SecureStorage.remove(key);
    } catch {
      // Already gone is the outcome we wanted.
    }
  }

  /**
   * Answered rather than thrown, and `[]` on failure, because the only caller is a launch deciding
   * whether a session exists. A store that cannot be listed holds nothing this launch can use, and
   * routing that to `noSession` leaves the Vault intact — where a throw would reach the machine as
   * `vaultUnavailable`, which is terminal and clears it.
   *
   * A Sign-out does not come through here, and must not: see {@link purge}.
   */
  async keys(): Promise<readonly string[]> {
    try {
      await this.#configure();
      return await SecureStorage.keys();
    } catch {
      return [];
    }
  }

  /**
   * Empty the namespace, and reject if the store did not.
   *
   * One native call, which is why this is a method on the port rather than a loop over {@link keys}
   * and {@link remove}: the store removes every item under the configured prefix itself, so there is
   * no key list that can come back short and no per-key failure to decide about. The tolerance the
   * two operations above have would be wrong here — see {@link Vault.purge}.
   *
   * **Deliberately not `SecureStorage.clear()`, which on Android empties the app's entire Keystore.**
   * Its own wrapper calls `clearItemsWithPrefix({ prefix })`; iOS reads `prefix`, Android reads
   * `_prefix` (`SecureStorage.java:99`, where the neighbouring `getPrefixedKeys` reads `prefix`), finds
   * nothing, and falls back to `""`. `clearKeyStore("")` then enumerates `keyStore.aliases()` and
   * deletes every one, because every string starts with the empty string. Naming both makes the single
   * call correct on both platforms.
   *
   * What that cost, before it was found: every sign-out deleted the biometric credential key. Its
   * ciphertext lives in the biometric plugin's own `SharedPreferences`, so it survived — and that
   * plugin's `getKey` mints a fresh key whenever its alias is missing rather than reporting it gone, so
   * every later read failed the GCM tag check permanently while `isCredentialsSaved` went on answering
   * `true` because it never decrypts. Enrolled biometric sign-in broke at the first sign-out, for good,
   * with no availability check anywhere able to see it.
   *
   * **No platform branch**, deliberately. Both names are correct everywhere — each platform reads the
   * one it looks for and ignores the other — so this stays a single path that is exercised on every
   * device, where a branch would leave each half untested on the other OS and both halves failing
   * silently when wrong: nothing cleared, or everything. It is also what upstream fixing the Android
   * name would want, since `prefix` is already being sent and `_prefix` simply goes inert.
   *
   * The cast is because `SecureStoragePlugin` declares only `clear()`. The method is real on both
   * platforms — `clear()` reaches native through it, which is how it did the damage.
   */
  async purge(): Promise<void> {
    try {
      await this.#configure();
      const store = SecureStorage as typeof SecureStorage & PrefixScopedClear;
      await store.clearItemsWithPrefix({ prefix: VAULT_KEY_PREFIX, _prefix: VAULT_KEY_PREFIX });
    } catch (error) {
      throw new VaultError(this.classify(error) ?? { kind: 'vaultUnavailable' }, 'Failed to empty the vault');
    }
  }

  /**
   * Configure inside each caller's `try`, never above it.
   *
   * Configuration is the first thing that can fail and the most consequential, because a rejection
   * escaping `keys()` reaches the machine as `vaultUnavailable` — terminal, and it clears the Vault.
   * Every operation therefore treats a configuration failure exactly as it treats a failure of its own
   * store call.
   */
  #configure(): Promise<void> {
    this.#configured ??= this.#configureOnce();
    return this.#configured;
  }

  /**
   * Only a *successful* configuration is remembered.
   *
   * `??=` on a rejected promise hands that same rejection to every later caller, so one transient
   * failure would take the Vault out for the remaining life of the process rather than for one call.
   */
  async #configureOnce(): Promise<void> {
    try {
      await SecureStorage.setKeyPrefix(VAULT_KEY_PREFIX);
      await SecureStorage.setDefaultKeychainAccess(KeychainAccess.whenUnlockedThisDeviceOnly);
    } catch (error) {
      this.#configured = null;
      throw error;
    }
  }

  /**
   * A missing item is `null`; anything else is a real failure and must not look like absence.
   *
   * Unlike the `@capgo` implementation this replaces, the diagnosis is not lost: `@aparajita` rejects
   * with a `code` drawn from `StorageErrorType` on every path, so absence and an unreadable store are
   * distinguishable.
   */
  private absentOrThrow(error: unknown): null {
    const cause = this.classify(error);
    if (cause === null) return null;
    throw new VaultError(cause);
  }

  /**
   * `null` means absence. Everything else is a Vault the app cannot use.
   *
   * There are no `unlock*` causes here and there cannot be: no read raises a prompt, so no read can
   * be cancelled, refused or locked out.
   */
  private classify(error: unknown): { kind: 'vaultUnavailable' } | null {
    const code = (error as { code?: unknown })?.code;
    if (code === StorageErrorType.missingKey) return null;
    return { kind: 'vaultUnavailable' };
  }
}
